/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
/**
 * GenerateReportDialog — global "Generate Report" pop-up (opened from the top nav bar).
 *
 * Scope/Filter + Analytics + Report Type + Template → POST /boardPaper/generate.
 * Generated reports appear in the Board Paper history (kept in the menu).
 *
 * Scope/filter defaults (per spec NOTE1): all risk categories, all watchlist risks, no horizon scan.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Divider,
  Switch,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Autocomplete,
  Chip,
  Checkbox,
  Collapse,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArticleIcon from "@mui/icons-material/Article";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import DualPlaneRadar from "pages/Anticipate/DualPlaneRadar";

const PURPLE = "#52009b";

const REPORT_TYPES = [
  {
    value: "strategic",
    label: "Strategic (Board Paper)",
    desc: "Strategic overview with appendices.",
  },
  {
    value: "operational",
    label: "Operational (Watching Brief)",
    desc: "Detailed reporting on each risk and Horizon Scan theme.",
  },
  {
    value: "tactical",
    label: "Tactical intervention (Action Report)",
    desc: "Action-oriented, prioritised recommendations for immediate response.",
  },
];

const FORECAST_OPTIONS = [
  { value: "cumulative", label: "Cumulative" },
  { value: "current", label: "Current state" },
  { value: "point-in-time", label: "Point in time" },
  { value: "date-range", label: "Date range" },
  { value: "comparison", label: "Comparison to prior paper" },
];

export default function GenerateReportDialog({ open, onClose, onGenerated }) {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const apiBase = protectedResources.apiTodoList.endpoint;

  const [risks, setRisks] = useState([]);
  const [horizonParams, setHorizonParams] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [reportType, setReportType] = useState("strategic");
  const [l1, setL1] = useState([]);
  const [l2, setL2] = useState([]);
  const [watchlistRiskIds, setWatchlistRiskIds] = useState([]);
  const [horizonParamIds, setHorizonParamIds] = useState([]);
  const [horizonPickerOpen, setHorizonPickerOpen] = useState(false); // ID141 — card-view param picker
  const [includeCharts, setIncludeCharts] = useState(false);
  const [anticipatory, setAnticipatory] = useState(false);
  const [actionOriented, setActionOriented] = useState(false);
  const [forecastHorizon, setForecastHorizon] = useState("cumulative");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [structure, setStructure] = useState("");
  // Output format (ID142) + reusable stored templates (ID144)
  const [outputFormat, setOutputFormat] = useState("docx");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("blank");
  const [uploadingTpl, setUploadingTpl] = useState(false);
  const [tplError, setTplError] = useState(null);
  // ID140 — category tree (categories with results in watchlist OR emerging themes)
  const [categoryTree, setCategoryTree] = useState([]); // [{ l1, l2: [] }]
  const [catPickerOpen, setCatPickerOpen] = useState(false); // ID140 — taxonomy picker pop-up
  const [expandedL1, setExpandedL1] = useState({}); // l1 name -> expanded in the picker
  const [radarRisks, setRadarRisks] = useState([]); // radar-data shape for the dual-plane export
  const radarRef = useRef(null); // off-screen DualPlaneRadar → PNG for the board paper
  const fileInputRef = useRef(null);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Tactical (Action Report) is always action-oriented (spec): force on + lock.
  useEffect(() => {
    if (reportType === "tactical") setActionOriented(true);
  }, [reportType]);

  // Load filter options when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setLoadingOptions(true);
    setError(null);
    setSuccess(null);
    Promise.all([
      executeRef.current("GET", `${apiBase}/emergingRisk/list`),
      executeRef.current("GET", `${apiBase}/companySearchParameters/list?withResults=1`),
      executeRef.current("GET", `${apiBase}/boardPaper/filter-categories`),
      executeRef.current("GET", `${apiBase}/reportTemplate/list`),
      executeRef.current("GET", `${apiBase}/emergingRisk/radar-data`),
    ])
      .then(([riskRes, paramRes, catRes, tplRes, radarRes]) => {
        // Only ACTIVE watchlist risks are selectable in the report (drafts are excluded).
        setRisks(
          Array.isArray(riskRes?.data) ? riskRes.data.filter((r) => r.status === "active") : []
        );
        setHorizonParams(Array.isArray(paramRes?.data) ? paramRes.data : []);
        setCategoryTree(Array.isArray(catRes?.data) ? catRes.data : []);
        setTemplates(Array.isArray(tplRes?.data) ? tplRes.data : []);
        setRadarRisks(Array.isArray(radarRes?.data) ? radarRes.data : []);
      })
      .catch(() => {})
      .finally(() => setLoadingOptions(false));
  }, [open, apiBase]);

  // ── ID140 — taxonomy picker (multi-select). Selecting an L1 includes all its L2 automatically;
  // individual L2 can also be ticked. l1[] holds whole-category selections, l2[] holds standalone
  // sub-category selections — matching the backend filter (riskCategoriesL1 / riskCategoriesL2).
  const toggleL1 = useCallback(
    (name) => {
      setL1((prev) => {
        if (prev.includes(name)) return prev.filter((x) => x !== name);
        return [...prev, name];
      });
      // When a whole L1 is (de)selected, drop any standalone L2 picks under it (now redundant).
      setL2((prevL2) => {
        const subs = categoryTree.find((c) => c.l1 === name)?.l2 || [];
        return prevL2.filter((s) => !subs.includes(s));
      });
    },
    [categoryTree]
  );

  const toggleL2 = useCallback((l2name) => {
    setL2((prev) => (prev.includes(l2name) ? prev.filter((x) => x !== l2name) : [...prev, l2name]));
  }, []);

  const totalSelected = l1.length + l2.length;

  // ── ID141 — Horizon Scan filter as a card-view pop-up. Company search parameters are grouped by
  // their `category` (the "card", e.g. "Supplier"); each card lists its individual parameters (e.g.
  // "Spotless"). You can Select all, tick a whole card, or tick individual parameters. The report
  // then includes only emerging themes linked to the selected parameters.
  const pidOf = (p) => p.id || p._id;
  const horizonGroups = useMemo(() => {
    const map = new Map();
    horizonParams.forEach((p) => {
      const cat = String(p.category || "Uncategorised").trim() || "Uncategorised";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(p);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([category, items]) => ({ category, items }));
  }, [horizonParams]);
  const horizonAllSelected =
    horizonParams.length > 0 && horizonParamIds.length === horizonParams.length;
  const selectedHorizonParams = horizonParams.filter((p) => horizonParamIds.includes(pidOf(p)));
  const toggleAllHorizon = () =>
    setHorizonParamIds(horizonAllSelected ? [] : horizonParams.map(pidOf));
  const toggleHorizonParam = (p) => {
    const id = pidOf(p);
    setHorizonParamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleHorizonCategory = (group) => {
    const ids = group.items.map(pidOf);
    const allIn = ids.every((id) => horizonParamIds.includes(id));
    setHorizonParamIds((prev) =>
      allIn ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))
    );
  };

  // ── Dual-plane radar for the board paper. When specific watchlist risks are selected, plot ONLY
  // those; otherwise plot all active risks. Rendered off-screen and captured to PNG on Generate.
  const radarExportRisks = useMemo(() => {
    if (!watchlistRiskIds.length) return radarRisks;
    const sel = new Set(watchlistRiskIds.map((x) => String(x)));
    return radarRisks.filter((r) => sel.has(String(r.id)));
  }, [radarRisks, watchlistRiskIds]);

  // A stored template only applies to its own format — reset the selection if the format changes.
  useEffect(() => {
    if (templateId === "blank") return;
    const sel = templates.find((t) => (t.id || t._id) === templateId);
    if (sel && sel.format !== outputFormat) setTemplateId("blank");
  }, [outputFormat, templates, templateId]);

  const handleTemplateUpload = useCallback(
    async (file) => {
      if (!file) return;
      const lower = file.name.toLowerCase();
      if (!lower.endsWith(".docx") && !lower.endsWith(".pptx")) {
        setTplError("Template must be a .docx or .pptx file.");
        return;
      }
      setUploadingTpl(true);
      setTplError(null);
      try {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const res = await executeRef.current("POST", `${apiBase}/reportTemplate`, {
          name: file.name.replace(/\.[^.]+$/, ""),
          fileName: file.name,
          fileBase64: b64,
        });
        if (!res?.success) throw new Error(res?.message || "Upload failed.");
        const listRes = await executeRef.current("GET", `${apiBase}/reportTemplate/list`);
        setTemplates(Array.isArray(listRes?.data) ? listRes.data : []);
        const newId = res.data?.id || res.data?._id;
        if (newId) {
          setTemplateId(newId);
          if (res.data?.format) setOutputFormat(res.data.format);
        }
      } catch (e) {
        setTplError(String(e.message || e));
      } finally {
        setUploadingTpl(false);
      }
    },
    [apiBase]
  );

  const handleTemplateDelete = useCallback(
    async (id) => {
      try {
        await executeRef.current("DELETE", `${apiBase}/reportTemplate/${id}`);
        setTemplates((prev) => prev.filter((t) => (t.id || t._id) !== id));
        setTemplateId((cur) => (cur === id ? "blank" : cur));
      } catch {
        /* ignore */
      }
    },
    [apiBase]
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      // Capture the dual-plane radar (off-screen) as a PNG for the "Watch List — Consequence by
      // Risk" chart when charts are included and there are risks to plot.
      let dualPlaneImage = null;
      if (includeCharts && radarExportRisks.length) {
        try {
          dualPlaneImage = await radarRef.current?.getPngBase64();
        } catch (e) {
          dualPlaneImage = null;
        }
      }
      const body = {
        reportType,
        riskCategoriesL1: l1,
        riskCategoriesL2: l2,
        watchlistRiskIds,
        horizonParamIds,
        scope: horizonParamIds.length ? "all" : "watch-list",
        includeCharts,
        dualPlaneImage,
        anticipatory,
        actionOriented: reportType === "tactical" ? true : actionOriented,
        forecastHorizon,
        dateMode: ["point-in-time", "date-range", "comparison"].includes(forecastHorizon)
          ? forecastHorizon
          : "current",
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        structure,
        outputFormat,
        templateId: templateId !== "blank" ? templateId : undefined,
      };
      const res = await executeRef.current("POST", `${apiBase}/boardPaper/generate`, body);
      if (!res?.success) throw new Error(res?.message || "Generation failed.");
      // Close THIS dialog before the report viewer opens. If both dialogs are open at once, MUI's two
      // focus traps fight and the viewer's editor can't be typed into (editing "not working" right
      // after generate). The parent shows its own "Report generated" message.
      setSuccess(null);
      onClose();
      if (onGenerated) onGenerated(res.data);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setGenerating(false);
    }
  }, [
    reportType,
    l1,
    l2,
    watchlistRiskIds,
    horizonParamIds,
    includeCharts,
    radarExportRisks,
    anticipatory,
    actionOriented,
    forecastHorizon,
    dateFrom,
    dateTo,
    structure,
    outputFormat,
    templateId,
    apiBase,
    onGenerated,
    onClose,
  ]);

  const showDates = ["point-in-time", "date-range", "comparison"].includes(forecastHorizon);

  return (
    <Dialog open={open} onClose={() => !generating && onClose()} maxWidth="md" fullWidth>
      {/* Off-screen dual-plane radar — mounted so its PNG can be captured for the board paper. */}
      {open && radarExportRisks.length > 0 && (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            left: -100000,
            top: 0,
            width: 760,
            pointerEvents: "none",
            opacity: 0,
          }}
        >
          <DualPlaneRadar
            ref={radarRef}
            risks={radarExportRisks}
            taxonomy={[]}
            selected={null}
            hovered={null}
            onSelect={() => {}}
            onHover={() => {}}
          />
        </Box>
      )}
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
      >
        <MDTypography variant="h5" sx={{ color: PURPLE, fontWeight: 700 }}>
          Generate Report
        </MDTypography>
        <IconButton onClick={() => !generating && onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Configure the parameters below, then click Generate. The report will be drafted by AI and
          produced as an editable Word or PowerPoint document for your review before any
          distribution.
        </MDTypography>

        {error && (
          <MDAlert color="error" dismissible sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </MDAlert>
        )}
        {success && (
          <MDAlert color="success" sx={{ mb: 2 }}>
            {success}
          </MDAlert>
        )}

        {/* ── Scope / Filter ── */}
        <MDTypography variant="subtitle2" sx={{ color: PURPLE, fontWeight: 700, mb: 1 }}>
          Scope / Filter
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/* ID140 — taxonomy picker pop-up (multi-select L1/L2) instead of two dropdowns. */}
            <MDTypography variant="caption" sx={{ fontWeight: 700, color: PURPLE }}>
              Risk categories
            </MDTypography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mt: 0.5,
                px: 1.5,
                py: 1,
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: "6px",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", flex: 1 }}
              >
                {totalSelected === 0 ? (
                  <MDTypography variant="button" color="text">
                    All categories
                  </MDTypography>
                ) : (
                  <>
                    {l1.map((name) => (
                      <Chip
                        key={`l1-${name}`}
                        label={name}
                        size="small"
                        onDelete={() => toggleL1(name)}
                        sx={{ bgcolor: "#ede7f6", color: PURPLE, fontWeight: 600 }}
                      />
                    ))}
                    {l2.map((name) => (
                      <Chip
                        key={`l2-${name}`}
                        label={name}
                        size="small"
                        variant="outlined"
                        onDelete={() => toggleL2(name)}
                      />
                    ))}
                  </>
                )}
              </Box>
              <MDButton
                size="small"
                variant="outlined"
                onClick={() => setCatPickerOpen(true)}
                disabled={loadingOptions}
                sx={{ color: PURPLE, borderColor: PURPLE, textTransform: "none", flexShrink: 0 }}
              >
                Select categories
              </MDButton>
            </Box>
          </Grid>

          {/* ID140 — taxonomy category picker pop-up */}
          <Dialog
            open={catPickerOpen}
            onClose={() => setCatPickerOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <span>Select risk categories</span>
              <IconButton size="small" onClick={() => setCatPickerOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <MDTypography variant="caption" color="text">
                  Tick a category to include all its sub-categories, or expand to pick individual
                  sub-categories.
                </MDTypography>
                {totalSelected > 0 && (
                  <MDButton
                    size="small"
                    variant="text"
                    onClick={() => {
                      setL1([]);
                      setL2([]);
                    }}
                    sx={{ color: PURPLE, textTransform: "none", flexShrink: 0 }}
                  >
                    Clear all
                  </MDButton>
                )}
              </Box>
              {categoryTree.length === 0 ? (
                <MDTypography variant="button" color="text">
                  No categories with results yet.
                </MDTypography>
              ) : (
                categoryTree.map((c) => {
                  const l1Checked = l1.includes(c.l1);
                  const childChecked = (c.l2 || []).filter((s) => l2.includes(s)).length;
                  const l1Open = !!expandedL1[c.l1];
                  return (
                    <Box key={c.l1} sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          size="small"
                          checked={l1Checked}
                          indeterminate={!l1Checked && childChecked > 0}
                          onChange={() => toggleL1(c.l1)}
                          sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                        />
                        <MDTypography variant="button" sx={{ flex: 1, fontWeight: 600 }}>
                          {c.l1}
                        </MDTypography>
                        {(c.l2 || []).length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedL1((p) => ({ ...p, [c.l1]: !p[c.l1] }))}
                          >
                            {l1Open ? (
                              <ExpandMoreIcon fontSize="small" />
                            ) : (
                              <ChevronRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </Box>
                      <Collapse in={l1Open}>
                        <Box sx={{ pl: 4, pb: 0.5 }}>
                          {(c.l2 || []).length === 0 ? (
                            <MDTypography variant="caption" color="text">
                              No sub-categories.
                            </MDTypography>
                          ) : (
                            (c.l2 || []).map((s) => (
                              <Box key={s} sx={{ display: "flex", alignItems: "center" }}>
                                <Checkbox
                                  size="small"
                                  checked={l1Checked || l2.includes(s)}
                                  disabled={l1Checked}
                                  onChange={() => toggleL2(s)}
                                  sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                                />
                                <MDTypography variant="button" color="text">
                                  {s}
                                </MDTypography>
                              </Box>
                            ))
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => setCatPickerOpen(false)}
                sx={{
                  bgcolor: PURPLE,
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#3d0075" },
                }}
              >
                Done
              </MDButton>
            </DialogActions>
          </Dialog>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" sx={{ fontWeight: 700, color: PURPLE }}>
              Watchlist Risks
            </MDTypography>
            <Box sx={{ mt: 0.5 }}>
              <Autocomplete
                multiple
                size="small"
                options={risks}
                getOptionLabel={(o) => o.Title || "Untitled"}
                isOptionEqualToValue={(o, v) => (o.id || o._id) === (v.id || v._id)}
                value={risks.filter((r) => watchlistRiskIds.includes(r.id || r._id))}
                onChange={(_, v) => setWatchlistRiskIds(v.map((r) => r.id || r._id))}
                loading={loadingOptions}
                renderInput={(p) => <TextField {...p} placeholder="All Watchlist Risks" />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" sx={{ fontWeight: 700, color: PURPLE }}>
              Horizon Scan Emerging Themes
            </MDTypography>
            {/* ID141 — pop-up card picker instead of a dropdown. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mt: 0.5,
                px: 1.5,
                py: 1,
                minHeight: 40,
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: "6px",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", flex: 1 }}
              >
                {horizonParamIds.length === 0 && (
                  <MDTypography variant="button" color="text">
                    None
                  </MDTypography>
                )}
                {horizonParamIds.length > 0 && horizonAllSelected && (
                  <Chip
                    label="All parameters"
                    size="small"
                    onDelete={() => setHorizonParamIds([])}
                    sx={{ bgcolor: "#ede7f6", color: PURPLE, fontWeight: 600 }}
                  />
                )}
                {horizonParamIds.length > 0 && !horizonAllSelected && (
                  <>
                    {selectedHorizonParams.slice(0, 3).map((p) => (
                      <Chip
                        key={pidOf(p)}
                        label={p.name || "Parameter"}
                        size="small"
                        onDelete={() => toggleHorizonParam(p)}
                      />
                    ))}
                    {selectedHorizonParams.length > 3 && (
                      <Chip size="small" label={`+${selectedHorizonParams.length - 3}`} />
                    )}
                  </>
                )}
              </Box>
              <MDButton
                size="small"
                variant="outlined"
                onClick={() => setHorizonPickerOpen(true)}
                disabled={loadingOptions}
                sx={{ color: PURPLE, borderColor: PURPLE, textTransform: "none", flexShrink: 0 }}
              >
                Select parameters
              </MDButton>
            </Box>
          </Grid>
        </Grid>

        {/* ID141 — Horizon Scan parameter picker pop-up (cards by collection/category) */}
        <Dialog
          open={horizonPickerOpen}
          onClose={() => setHorizonPickerOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>Select Horizon Scan parameters</span>
            <IconButton size="small" onClick={() => setHorizonPickerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <MDTypography variant="caption" color="text">
                Tick a whole card to include all its parameters, or tick individual ones. The report
                will include only emerging themes linked to the selected parameters.
              </MDTypography>
              <FormControlLabel
                sx={{ flexShrink: 0, ml: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={horizonAllSelected}
                    indeterminate={horizonParamIds.length > 0 && !horizonAllSelected}
                    onChange={toggleAllHorizon}
                    sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                  />
                }
                label={
                  <MDTypography variant="button" sx={{ fontWeight: 700, color: PURPLE }}>
                    Select all
                  </MDTypography>
                }
              />
            </Box>
            {horizonParams.length === 0 ? (
              <MDTypography variant="button" color="text">
                No company search parameters configured.
              </MDTypography>
            ) : (
              <Grid container spacing={1.5}>
                {horizonGroups.map((group) => {
                  const ids = group.items.map(pidOf);
                  const selectedCount = ids.filter((id) => horizonParamIds.includes(id)).length;
                  const allInCat = selectedCount === ids.length;
                  return (
                    <Grid item xs={12} sm={6} key={group.category}>
                      <Box
                        sx={{
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 2,
                          p: 1.25,
                          height: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                            pb: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={allInCat}
                            indeterminate={selectedCount > 0 && !allInCat}
                            onChange={() => toggleHorizonCategory(group)}
                            sx={{ p: 0.5, color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                          />
                          <MDTypography variant="button" sx={{ fontWeight: 700 }}>
                            {group.category}
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({group.items.length})
                          </MDTypography>
                        </Box>
                        {group.items.map((p) => (
                          <Box key={pidOf(p)} sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                            <Checkbox
                              size="small"
                              checked={horizonParamIds.includes(pidOf(p))}
                              onChange={() => toggleHorizonParam(p)}
                              sx={{ p: 0.5, color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                            />
                            <MDTypography variant="button" color="text">
                              {p.name || p.label || "Parameter"}
                            </MDTypography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => setHorizonPickerOpen(false)}
              sx={{
                bgcolor: PURPLE,
                color: "#fff",
                textTransform: "none",
                "&:hover": { bgcolor: "#3d0075" },
              }}
            >
              Done
            </MDButton>
          </DialogActions>
        </Dialog>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={3}>
          {/* ── Analytics ── */}
          <Grid item xs={12} sm={6}>
            <MDTypography variant="subtitle2" sx={{ color: PURPLE, fontWeight: 700, mb: 1 }}>
              Analytics
            </MDTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={includeCharts}
                  onChange={(e) => setIncludeCharts(e.target.checked)}
                />
              }
              label="Include Charts"
            />
            <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Include relevant charts based on your filter criteria.
            </MDTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={anticipatory}
                  onChange={(e) => setAnticipatory(e.target.checked)}
                />
              }
              label="Anticipatory"
            />
            <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              AI projection of where the risks / themes are heading.
            </MDTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={actionOriented}
                  disabled={reportType === "tactical"}
                  onChange={(e) => setActionOriented(e.target.checked)}
                />
              }
              label="Action-oriented"
            />
            <MDTypography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {reportType === "tactical"
                ? "Always on for an Action Report."
                : "AI recommendations and actions, or recommendations omitted."}
            </MDTypography>

            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Forecast Horizon</InputLabel>
              <Select
                value={forecastHorizon}
                label="Forecast Horizon"
                onChange={(e) => setForecastHorizon(e.target.value)}
              >
                {FORECAST_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {showDates && (
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <TextField
                  size="small"
                  type="date"
                  label={forecastHorizon === "date-range" ? "From" : "As at"}
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  fullWidth
                />
                {forecastHorizon === "date-range" && (
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    fullWidth
                  />
                )}
              </Box>
            )}

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              sx={{ mt: 2 }}
              label="Structure description (optional)"
              placeholder="e.g. half-page executive summary, up to 2 pages of body, the rest in appendices."
              value={structure}
              onChange={(e) => setStructure(e.target.value)}
            />
          </Grid>

          {/* ── Report Type ── */}
          <Grid item xs={12} sm={6}>
            <FormControl>
              <FormLabel
                sx={{
                  color: PURPLE,
                  fontWeight: 700,
                  fontSize: 14,
                  "&.Mui-focused": { color: PURPLE },
                }}
              >
                Report Type
              </FormLabel>
              <RadioGroup value={reportType} onChange={(e) => setReportType(e.target.value)}>
                {REPORT_TYPES.map((rt) => (
                  <Box key={rt.value} sx={{ mb: 1 }}>
                    <FormControlLabel
                      value={rt.value}
                      control={<Radio sx={{ "&.Mui-checked": { color: PURPLE } }} />}
                      label={rt.label}
                    />
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", ml: 4, mt: -0.5 }}
                    >
                      {rt.desc}
                    </MDTypography>
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Output format & Template ── */}
        <MDTypography variant="subtitle2" sx={{ color: PURPLE, fontWeight: 700, mb: 1 }}>
          Output format &amp; Template
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Output format</InputLabel>
              <Select
                value={outputFormat}
                label="Output format"
                onChange={(e) => setOutputFormat(e.target.value)}
              >
                <MenuItem value="docx">Word (.docx)</MenuItem>
                <MenuItem value="pptx">PowerPoint (.pptx)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select
                value={templateId}
                label="Template"
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <MenuItem value="blank">Blank (default formatting)</MenuItem>
                {templates
                  .filter((t) => t.format === outputFormat)
                  .map((t) => (
                    <MenuItem key={t.id || t._id} value={t.id || t._id}>
                      {t.name} · {t.format.toUpperCase()}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.pptx"
            style={{ display: "none" }}
            onChange={(e) => {
              handleTemplateUpload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <MDButton
            variant="outlined"
            size="small"
            disabled={uploadingTpl}
            sx={{ borderColor: PURPLE, color: PURPLE }}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadingTpl ? "Uploading…" : "Upload & store a template (.docx / .pptx)…"}
          </MDButton>
          {templates
            .filter((t) => t.format === outputFormat)
            .map((t) => {
              const tid = t.id || t._id;
              return (
                <Chip
                  key={tid}
                  size="small"
                  label={t.name}
                  color={templateId === tid ? "secondary" : "default"}
                  onClick={() => setTemplateId(tid)}
                  onDelete={() => handleTemplateDelete(tid)}
                />
              );
            })}
        </Box>
        {tplError && (
          <MDTypography variant="caption" sx={{ color: "#c62828", display: "block", mt: 0.5 }}>
            {tplError}
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Stored templates are reusable across reports. A Word template&apos;s fonts, headings,
          margins and header/footer are applied to the output; the content is based on the criteria
          above.
        </MDTypography>

        <MDBox
          mt={2}
          p={1.5}
          sx={{ bgcolor: "#fff8f0", borderRadius: 1, borderLeft: "3px solid #f57c00" }}
        >
          <MDTypography variant="caption" sx={{ color: "#8a5000" }}>
            ⚠ The generated report is AI-drafted and must be reviewed before distribution.
          </MDTypography>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <MDButton variant="text" onClick={onClose} disabled={generating}>
          Cancel
        </MDButton>
        <MDButton
          variant="contained"
          disabled={generating}
          onClick={handleGenerate}
          sx={{ bgcolor: PURPLE, color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
          startIcon={
            generating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArticleIcon />
          }
        >
          {generating ? "Generating…" : "Generate Report"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
